import React, { useState } from "react";

import {
  Archive,
  CalendarCheck,
  CaretRight,
  Gauge,
  LinkBreak,
  PencilSimple,
  Plus,
  ShieldCheck,
  WarningCircle,
  Wrench,
} from "@phosphor-icons/react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  gmao,
  machineCatalog as machineCatalogApi,
  prospects as prospectsApi,
} from "@/api";
import MachineAccessoryLinkModal from "@/components/gmao/MachineAccessoryLinkModal";
import MachineArchiveModal from "@/components/gmao/MachineArchiveModal";
import MachineFormModal from "@/components/gmao/MachineFormModal";
import MachineIdentityEditModal from "@/components/gmao/MachineIdentityEditModal";
import MachineMeterModal from "@/components/gmao/MachineMeterModal";
import MachineSupportContextPanel from "@/components/gmao/MachineSupportContextPanel";
import MaintenancePlanModal from "@/components/gmao/MaintenancePlanModal";
import { useAuth } from "@/context/AuthContext";
import { useMachineIdentityDetail } from "@/hooks/useMachineIdentityDetail";
import { hasPageAccess } from "@/lib/accessPermissions";
import { formatApiError } from "@/lib/api";
import {
  formatMachineDate,
  formatMachineDateTime,
  maintenanceDetail,
  serviceEventLabel,
  serviceStatusLabel,
} from "@/lib/gmaoMachineLifecycle";
import {
  canArchiveMachine,
  canEditMachine,
  canUpdateMachineMeter,
} from "@/lib/gmaoMachinePermissions";

const MACHINE_STATUS_LABELS = {
  active: "Opérationnelle",
  maintenance: "En maintenance",
  inactive: "Inactive",
  retired: "Hors service",
};

const EQUIPMENT_TYPE_LABELS = {
  machine: "Machine",
  accessory: "Accessoire",
  handpiece: "Pièce à main",
};

const LOCATION_STATUS_LABELS = {
  in_stock: "En stock",
  reserved: "Réservée",
  installed: "Installée",
  returned: "Retournée",
};

function badgeClass(status) {
  if (status === "active") {
    return "border-[var(--do-success-border)] bg-[var(--do-success-soft)] text-[var(--do-success)]";
  }

  if (status === "maintenance") {
    return "border-[var(--do-warning-border)] bg-[var(--do-warning-soft)] text-[var(--do-warning)]";
  }

  if (status === "retired") {
    return "border-[var(--do-danger-border)] bg-[var(--do-danger-soft)] text-[var(--do-danger)]";
  }

  return "border-[var(--do-border)] bg-[var(--do-bg-2)] text-[var(--do-muted)]";
}

function DetailItem({ label, value }) {
  return (
    <div>
      <dt className="text-xs font-heading font-bold uppercase tracking-wide text-[var(--do-muted)]">
        {label}
      </dt>
      <dd className="mt-1 text-sm font-medium text-[var(--do-ink)]">
        {value || "Non renseigné"}
      </dd>
    </div>
  );
}

function LifecycleCard({ icon: Icon, label, value, detail, warning = false }) {
  return (
    <article className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4">
      <div className="flex items-start gap-3">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
            warning
              ? "bg-[var(--do-warning-soft)] text-[var(--do-warning)]"
              : "bg-[var(--do-accent-soft)] text-[var(--do-accent)]"
          }`}
        >
          <Icon size={20} weight="bold" />
        </div>
        <div className="min-w-0">
          <div className="text-xs font-heading font-bold uppercase tracking-wide text-[var(--do-muted)]">
            {label}
          </div>
          <div className="mt-1 font-heading text-base font-black">{value}</div>
          {detail && (
            <div className="mt-1 text-sm text-[var(--do-muted)]">{detail}</div>
          )}
        </div>
      </div>
    </article>
  );
}

export default function GmaoMachineDetailV2() {
  const { machineId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const {
    machine,
    accessories,
    nextMaintenance,
    currentServiceEvent,
    maintenanceAvailable,
    loading,
    error,
    reload,
  } = useMachineIdentityDetail(machineId, user);

  const [editOpen, setEditOpen] = useState(false);
  const [handpieceOpen, setHandpieceOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [meterOpen, setMeterOpen] = useState(false);
  const [maintenanceOpen, setMaintenanceOpen] = useState(false);
  const [accessoryLinkOpen, setAccessoryLinkOpen] = useState(false);
  const [accessoryCreateOpen, setAccessoryCreateOpen] = useState(false);
  const [accessoryArchiveTarget, setAccessoryArchiveTarget] = useState(null);
  const [accessoryOptions, setAccessoryOptions] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [catalogLoadError, setCatalogLoadError] = useState("");
  const [prospectsLoaded, setProspectsLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [meterSaving, setMeterSaving] = useState(false);
  const [maintenanceSaving, setMaintenanceSaving] = useState(false);
  const [accessorySaving, setAccessorySaving] = useState(false);

  const mayEdit = canEditMachine(user) && !machine?.isArchived;
  const mayArchive = canArchiveMachine(user) && !machine?.isArchived;
  const mayUpdateMeter =
    canUpdateMachineMeter(user) &&
    hasPageAccess(user, "gmao", "maintenance") &&
    !machine?.isArchived;

  const mayManageMaintenance =
    ["admin", "manager", "maintenance_manager"].includes(
      user?.actualRole || user?.role
    ) &&
    hasPageAccess(user, "gmao", "maintenance") &&
    machine?.locationStatus === "installed" &&
    !machine?.isArchived;

  const openEditor = async () => {
    if (!mayEdit) return;

    if (!prospectsLoaded) {
      try {
        const data = await prospectsApi.list();
        setProspects(Array.isArray(data) ? data : []);
        setProspectsLoaded(true);
      } catch (requestError) {
        toast.error(
          formatApiError(requestError.response?.data?.detail) ||
            "Impossible de charger les clients."
        );
      }
    }

    setEditOpen(true);
  };

  const loadCatalogByType = async (equipmentType) => {
    try {
      const items = await machineCatalogApi.list();

      const filtered = Array.isArray(items)
        ? items.filter(
            (item) =>
              item.active !== false &&
              item.type === equipmentType &&
              (
                equipmentType !== "handpiece" ||
                (
                  Array.isArray(item.compatibleMachineIds) &&
                  item.compatibleMachineIds.includes(
                    machine.machineCatalogId
                  )
                )
              )
          )
        : [];

      setCatalogItems(filtered);
      setCatalogLoadError("");
    } catch {
      setCatalogItems([]);
      setCatalogLoadError("catalog_load_failed");
    }
  };


  const loadHandpieceCatalog = () =>
    loadCatalogByType("handpiece");

  const openHandpieceCreator = async () => {
    if (!mayEdit || machine.equipmentType !== "machine") return;

    const requests = [loadHandpieceCatalog()];

    if (!prospectsLoaded) {
      requests.push(
        prospectsApi
          .list()
          .then((data) => {
            setProspects(Array.isArray(data) ? data : []);
            setProspectsLoaded(true);
          })
          .catch(() => {
            setProspects([]);
          })
      );
    }

    await Promise.all(requests);
    setHandpieceOpen(true);
  };

  const saveMachine = async (payload) => {
    setSaving(true);

    try {
      await gmao.updateMachine(machine.id, payload);
      toast.success("Machine mise à jour");
      setEditOpen(false);
      await reload();
    } catch (saveError) {
      toast.error(
        formatApiError(saveError.response?.data?.detail) ||
          "Impossible de modifier la machine."
      );
    } finally {
      setSaving(false);
    }
  };

  const openAccessoryManager = async () => {
    if (!mayEdit || machine.equipmentType !== "machine") {
      return;
    }

    setAccessorySaving(true);

    try {
      const items = await gmao.listMachines({
        limit: 500,
      });

      setAccessoryOptions(
        Array.isArray(items)
          ? items
          : []
      );
      setAccessoryLinkOpen(true);
    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError.response?.data?.detail
        ) ||
          "Impossible de charger les accessoires disponibles."
      );
    } finally {
      setAccessorySaving(false);
    }
  };


  const openAccessoryCreator = async () => {
    setAccessoryLinkOpen(false);

    const requests = [
      loadCatalogByType("accessory"),
    ];

    if (!prospectsLoaded) {
      requests.push(
        prospectsApi
          .list()
          .then((data) => {
            setProspects(
              Array.isArray(data)
                ? data
                : []
            );
            setProspectsLoaded(true);
          })
          .catch(() => {
            setProspects([]);
          })
      );
    }

    await Promise.all(requests);
    setAccessoryCreateOpen(true);
  };


  const createHandpiece = async (payload) => {
    setSaving(true);

    try {
      await gmao.createMachine(payload);
      toast.success("Pièce à main ajoutée");
      setHandpieceOpen(false);
      await reload();
    } catch (saveError) {
      toast.error(
        formatApiError(saveError.response?.data?.detail) ||
          "Impossible d'ajouter la pièce à main."
      );
    } finally {
      setSaving(false);
    }
  };

  const createAccessory = async (payload) => {
    setAccessorySaving(true);

    try {
      await gmao.createMachine(payload);
      toast.success("Accessoire ajouté");
      setAccessoryCreateOpen(false);
      await reload();
    } catch (saveError) {
      toast.error(
        formatApiError(
          saveError.response?.data?.detail
        ) ||
          "Impossible d'ajouter l'accessoire."
      );
    } finally {
      setAccessorySaving(false);
    }
  };


  const linkAccessory = async (accessoryId) => {
    if (!accessoryId) {
      return;
    }

    setAccessorySaving(true);

    try {
      await gmao.linkEquipmentParentMachine(
        accessoryId,
        {
          parent_machine_id: machine.id,
          note: "Rattaché depuis la carte machine",
        }
      );
      toast.success("Accessoire rattaché");
      setAccessoryLinkOpen(false);
      await reload();
    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError.response?.data?.detail
        ) ||
          "Impossible de rattacher l'accessoire."
      );
    } finally {
      setAccessorySaving(false);
    }
  };


  const detachAccessory = async (accessory) => {
    setAccessorySaving(true);

    try {
      await gmao.linkEquipmentParentMachine(
        accessory.id,
        {
          parent_machine_id: null,
          note: "Détaché depuis la carte machine",
        }
      );
      toast.success("Accessoire détaché");
      await reload();
    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError.response?.data?.detail
        ) ||
          "Impossible de détacher l'accessoire."
      );
    } finally {
      setAccessorySaving(false);
    }
  };


  const archiveAccessory = async () => {
    if (!accessoryArchiveTarget) {
      return;
    }

    setAccessorySaving(true);

    try {
      await gmao.archiveMachine(
        accessoryArchiveTarget.id
      );
      toast.success("Accessoire archivé");
      setAccessoryArchiveTarget(null);
      await reload();
    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError.response?.data?.detail
        ) ||
          "Impossible d'archiver l'accessoire."
      );
    } finally {
      setAccessorySaving(false);
    }
  };


  const saveMaintenancePlan = async (payload) => {
    setMaintenanceSaving(true);

    try {
      if (nextMaintenance?.id) {
        await gmao.updateMaintenancePlan(
          nextMaintenance.id,
          payload
        );
        toast.success(
          "Plan de maintenance modifié"
        );
      } else {
        await gmao.createMaintenancePlan(
          payload
        );
        toast.success(
          "Plan de maintenance créé"
        );
      }

      setMaintenanceOpen(false);
      await reload();
    } catch (requestError) {
      toast.error(
        formatApiError(
          requestError.response?.data?.detail
        ) ||
          "Impossible d'enregistrer le plan de maintenance."
      );
    } finally {
      setMaintenanceSaving(false);
    }
  };


  const archiveMachine = async () => {
    setArchiving(true);

    try {
      await gmao.archiveMachine(machine.id);
      toast.success("Machine archivée");
      navigate("/app/gmao/v2/parc");
    } catch (archiveError) {
      toast.error(
        formatApiError(archiveError.response?.data?.detail) ||
          "Impossible d'archiver la machine."
      );
      setArchiving(false);
    }
  };

  const saveMeter = async (targetMachineId, payload) => {
    setMeterSaving(true);

    try {
      await gmao.updateMachineMeter(targetMachineId, payload);
      toast.success("Compteur mis à jour");
      setMeterOpen(false);
      await reload();
    } catch (meterError) {
      toast.error(
        formatApiError(meterError.response?.data?.detail) ||
          "Impossible de mettre à jour le compteur."
      );
    } finally {
      setMeterSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8" data-testid="gmao-machine-detail-v2-loading">
        <div className="label-eyebrow lirah-pulse">Chargement de la machine</div>
      </div>
    );
  }

  if (error || !machine) {
    return (
      <div className="max-w-3xl p-4 sm:p-6 lg:p-8">
        <Link to="/app/gmao/v2/parc" className="text-sm font-heading font-semibold text-[var(--do-accent)]">
          Retour au parc
        </Link>
        <div className="mt-5 lirah-card p-8">
          <h1 className="font-heading text-xl font-black">Équipement indisponible</h1>
          <p className="mt-2 text-sm text-[var(--do-muted)]">{error}</p>
        </div>
      </div>
    );
  }

  const location =
    [
      machine.address,
      [machine.postalCode, machine.city].filter(Boolean).join(" "),
    ]
      .filter(Boolean)
      .join(" · ") ||
    machine.stockLocation ||
    "Non renseignée";

  const maintenanceValue = !maintenanceAvailable
    ? "Non disponible"
    : nextMaintenance?.nextDueAt
      ? formatMachineDate(nextMaintenance.nextDueAt)
      : nextMaintenance?.nextDueMeterHours != null
        ? `À ${nextMaintenance.nextDueMeterHours} h`
        : "Aucune planifiée";

  const serviceValue = currentServiceEvent
    ? serviceEventLabel(currentServiceEvent)
    : "Aucune";

  const serviceDetail = currentServiceEvent
    ? [
        serviceStatusLabel(currentServiceEvent.status),
        currentServiceEvent.scheduledStart
          ? formatMachineDate(currentServiceEvent.scheduledStart)
          : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "Aucune panne ou réparation en cours";

  const handpieces = accessories.filter(
    (item) => item.equipmentType === "handpiece"
  );

  const linkedAccessories = accessories.filter(
    (item) => item.equipmentType !== "handpiece"
  );

  return (
    <main
      className="acta-gmao-page-standard w-full max-w-full min-w-0 p-4 sm:p-5 lg:p-8"
      data-testid="gmao-machine-detail-v2-page"
    >
      <header className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2 text-sm text-[var(--do-muted)]">
            <Link to="/app/gmao/v2/parc" className="font-semibold text-[var(--do-accent)]">
              Parc
            </Link>
            <span>/</span>
            <span>{machine.name}</span>
          </div>
          <h1 className="mt-3 font-heading text-3xl font-black tracking-tight sm:text-4xl">
            {machine.name}
          </h1>
          <p className="mt-1 text-sm text-[var(--do-muted)]">
            Carte d'identité de la machine
          </p>
        </div>

        {(mayUpdateMeter || mayEdit || mayArchive) && (
          <div className="flex flex-wrap items-center gap-2">
            {mayUpdateMeter && (
              <button
                type="button"
                onClick={() => setMeterOpen(true)}
                className="inline-flex items-center gap-2 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-2.5 text-sm font-heading font-bold"
                data-testid="machine-detail-meter"
              >
                <Gauge size={17} weight="bold" />
                Mettre à jour le compteur
              </button>
            )}
            {mayEdit && (
              <button
                type="button"
                onClick={openEditor}
                className="inline-flex items-center gap-2 rounded-xl bg-[var(--do-accent)] px-4 py-2.5 text-sm font-heading font-bold text-[var(--do-on-accent)]"
                data-testid="machine-detail-edit"
              >
                <PencilSimple size={17} weight="bold" />
                Modifier
              </button>
            )}
            {mayArchive && (
              <button
                type="button"
                onClick={() => setArchiveOpen(true)}
                className="inline-flex items-center gap-2 rounded-xl border border-[var(--do-danger-border)] bg-[var(--do-danger-soft)] px-4 py-2.5 text-sm font-heading font-bold text-[var(--do-danger)]"
                data-testid="machine-detail-archive"
              >
                <Archive size={17} weight="bold" />
                Archiver
              </button>
            )}
          </div>
        )}
      </header>

      <section className="mt-6 lirah-card p-5 sm:p-6" data-testid="machine-detail-identity">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
          <div className="flex min-w-0 flex-1 items-start gap-4">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-[var(--do-accent-soft)] text-[var(--do-accent)]">
              <Wrench size={34} />
            </div>
            <div className="min-w-0">
              <h2 className="font-heading text-xl font-black">{machine.name}</h2>
              <div className="mt-1 text-sm text-[var(--do-muted)]">
                {[machine.manufacturer, machine.model].filter(Boolean).join(" · ") || "Modèle non renseigné"}
              </div>
              <div className="mt-2 text-sm">
                N° de série : <span className="font-mono-stat">{machine.serialNumber || "—"}</span>
              </div>
              <span className={`mt-3 inline-flex rounded-full border px-3 py-1 text-xs font-heading font-bold ${badgeClass(machine.status)}`}>
                {MACHINE_STATUS_LABELS[machine.status] || machine.status}
              </span>
            </div>
          </div>

          <dl className="grid flex-1 gap-4 sm:grid-cols-2">
            <DetailItem label="Type" value={EQUIPMENT_TYPE_LABELS[machine.equipmentType] || machine.equipmentType} />
            <DetailItem label="Référence catalogue" value={machine.catalogReference || "—"} />
            <DetailItem label="Fabricant" value={machine.manufacturer || "—"} />
            <DetailItem label="Modèle" value={machine.model || "—"} />
          </dl>
        </div>
      </section>

      <section className="mt-4 lirah-card p-5 sm:p-6" data-testid="machine-detail-assignment">
        <h2 className="font-heading text-lg font-black">Situation et localisation</h2>
        <dl className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <DetailItem label="Situation" value={LOCATION_STATUS_LABELS[machine.locationStatus] || machine.locationStatus} />
          <DetailItem label="Site" value={machine.siteName || "Non renseigné"} />
          <DetailItem label="Localisation" value={location} />
          <DetailItem
            label="Entrée en stock"
            value={
              ["in_stock", "returned"].includes(machine.locationStatus)
                ? formatMachineDate(machine.stockReceivedAt)
                : "—"
            }
          />
        </dl>
      </section>

      <MachineSupportContextPanel
        machine={machine}
        user={user}
      />

      <section className="mt-4 lirah-card p-5 sm:p-6" data-testid="machine-detail-lifecycle">
        <div className="flex items-center gap-2">
          <ShieldCheck size={20} weight="bold" className="text-[var(--do-accent)]" />
          <h2 className="font-heading text-lg font-black">Cycle de vie</h2>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <LifecycleCard
            icon={CalendarCheck}
            label="Mise en service"
            value={formatMachineDate(machine.commissioningDate)}
          />
          <LifecycleCard
            icon={ShieldCheck}
            label="Fin de garantie"
            value={formatMachineDate(machine.warrantyEndDate)}
          />
          <LifecycleCard
            icon={Gauge}
            label="Heures de fonctionnement"
            value={`${Number(machine.operatingHours || 0).toLocaleString("fr-FR")} h`}
            detail={
              machine.meterUpdatedAt
                ? `Mis à jour le ${formatMachineDateTime(machine.meterUpdatedAt)}`
                : "Aucun relevé enregistré"
            }
          />
          <LifecycleCard
            icon={CalendarCheck}
            label="Prochaine maintenance"
            value={maintenanceValue}
            detail={
              maintenanceAvailable
                ? maintenanceDetail(nextMaintenance)
                : "Accès maintenance non disponible"
            }
          />
          <LifecycleCard
            icon={currentServiceEvent ? WarningCircle : ShieldCheck}
            label="Panne / réparation"
            value={serviceValue}
            detail={serviceDetail}
            warning={Boolean(currentServiceEvent)}
          />
        </div>

        {machine.notes && (
          <div className="mt-4 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-2)] p-4">
            <div className="text-xs font-heading font-bold uppercase tracking-wide text-[var(--do-muted)]">Notes</div>
            <p className="mt-1 whitespace-pre-wrap text-sm">{machine.notes}</p>
          </div>
        )}
      </section>

      <section
        className="mt-4 lirah-card p-5 sm:p-6"
        data-testid="machine-detail-maintenance"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="label-eyebrow">
              Maintenance préventive
            </div>
            <h2 className="mt-1 font-heading text-lg font-black">
              Plan de maintenance
            </h2>
          </div>

          {mayManageMaintenance && (
            <button
              type="button"
              onClick={() => setMaintenanceOpen(true)}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--do-accent)] px-3 py-2 text-sm font-heading font-bold text-[var(--do-on-accent)]"
              data-testid="machine-detail-maintenance-action"
            >
              <CalendarCheck
                size={17}
                weight="bold"
              />
              {nextMaintenance
                ? "Modifier le plan"
                : "Planifier une maintenance"}
            </button>
          )}
        </div>

        {maintenanceAvailable ? (
          nextMaintenance ? (
            <dl className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <DetailItem
                label="Plan"
                value={nextMaintenance.name}
              />
              <DetailItem
                label="Périodicité"
                value={
                  nextMaintenance.scheduleType === "meter"
                    ? `Tous les ${nextMaintenance.intervalValue} h`
                    : `Tous les ${nextMaintenance.intervalValue} ${nextMaintenance.intervalUnit}`
                }
              />
              <DetailItem
                label="Prochaine échéance"
                value={maintenanceValue}
              />
              <DetailItem
                label="Priorité"
                value={nextMaintenance.priority || "Normale"}
              />
            </dl>
          ) : (
            <p className="mt-4 text-sm text-[var(--do-muted)]">
              Aucun plan de maintenance actif pour cette machine.
            </p>
          )
        ) : (
          <p className="mt-4 text-sm text-[var(--do-muted)]">
            Accès à la maintenance non disponible pour ce rôle.
          </p>
        )}
      </section>

      <section className="mt-4 lirah-card p-5 sm:p-6" data-testid="machine-detail-system">
        <h2 className="font-heading text-lg font-black">Informations système</h2>
        <dl className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <DetailItem label="Créée le" value={formatMachineDateTime(machine.createdAt)} />
          <DetailItem label="Dernière modification" value={formatMachineDateTime(machine.updatedAt)} />
          <DetailItem label="État d'archivage" value={machine.isArchived ? "Archivée" : "Active"} />
          <DetailItem label="Référence interne" value={machine.id} />
        </dl>
      </section>

      {machine.equipmentType === "machine" && (
        <section
          className="mt-4 overflow-hidden lirah-card"
          data-testid="machine-detail-v2-handpieces"
        >
          <div className="flex items-center justify-between gap-3 border-b border-[var(--do-border)] px-5 py-4">
            <div>
              <h2 className="font-heading text-lg font-black">Pièces à main</h2>
              <p className="mt-1 text-xs text-[var(--do-muted)]">
                {handpieces.length} pièce{handpieces.length > 1 ? "s" : ""} rattachée{handpieces.length > 1 ? "s" : ""}
              </p>
            </div>

            {mayEdit && (
              <button
                type="button"
                onClick={openHandpieceCreator}
                className="rounded-lg bg-[var(--do-accent)] px-3 py-2 text-sm font-heading font-bold text-[var(--do-on-accent)]"
                data-testid="machine-detail-add-handpiece"
              >
                Ajouter une pièce à main
              </button>
            )}
          </div>

          {handpieces.length === 0 ? (
            <div className="p-6 text-center text-sm text-[var(--do-muted)]">
              Aucune pièce à main rattachée à cette machine.
            </div>
          ) : (
            <div className="divide-y divide-[var(--do-border)]">
              {handpieces.map((handpiece) => (
                <Link
                  key={handpiece.id}
                  to={`/app/gmao/v2/parc/${encodeURIComponent(handpiece.id)}`}
                  className="grid gap-2 px-5 py-3.5 text-sm hover:bg-[var(--do-bg-2)] sm:grid-cols-[1.4fr_1fr_auto] sm:items-center"
                >
                  <span className="font-heading font-bold">{handpiece.name}</span>
                  <span className="font-mono-stat text-xs text-[var(--do-muted)]">
                    N° de série : {handpiece.serialNumber || "—"}
                  </span>
                  <span className="flex items-center justify-between gap-3">
                    <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-heading font-semibold ${badgeClass(handpiece.status)}`}>
                      {MACHINE_STATUS_LABELS[handpiece.status] || handpiece.status}
                    </span>
                    <CaretRight size={17} />
                  </span>
                </Link>
              ))}
            </div>
          )}
        </section>
      )}

      <section className="mt-4 overflow-hidden lirah-card" data-testid="machine-detail-v2-accessories">
        <div className="flex items-center justify-between gap-3 border-b border-[var(--do-border)] px-5 py-4">
          <div>
            <h2 className="font-heading text-lg font-black">Accessoires liés</h2>
            <p className="mt-1 text-xs text-[var(--do-muted)]">
              {linkedAccessories.length} accessoire{linkedAccessories.length > 1 ? "s" : ""} rattaché{linkedAccessories.length > 1 ? "s" : ""}
            </p>
          </div>

          {mayEdit && machine.equipmentType === "machine" && (
            <button
              type="button"
              onClick={openAccessoryManager}
              disabled={accessorySaving}
              className="inline-flex items-center gap-2 rounded-lg bg-[var(--do-accent)] px-3 py-2 text-sm font-heading font-bold text-[var(--do-on-accent)] disabled:opacity-50"
              data-testid="machine-detail-add-accessory"
            >
              <Plus size={17} weight="bold" />
              Ajouter un accessoire
            </button>
          )}
        </div>

        {linkedAccessories.length === 0 ? (
          <div className="p-6 text-center text-sm text-[var(--do-muted)]">
            Aucun accessoire rattaché à cette machine.
          </div>
        ) : (
          <div className="divide-y divide-[var(--do-border)]">
            {linkedAccessories.map((accessory) => (
              <div
                key={accessory.id}
                className="grid gap-3 px-5 py-3.5 text-sm sm:grid-cols-[1.2fr_1fr_auto_auto] sm:items-center"
              >
                <span className="font-heading font-bold">
                  {accessory.name}
                </span>

                <span className="font-mono-stat text-xs text-[var(--do-muted)]">
                  N° de série : {accessory.serialNumber || "—"}
                </span>

                <span className={`inline-flex w-fit rounded-full border px-2.5 py-1 text-xs font-heading font-semibold ${badgeClass(accessory.status)}`}>
                  {MACHINE_STATUS_LABELS[accessory.status] || accessory.status}
                </span>

                <div className="flex flex-wrap justify-end gap-2">
                  <Link
                    to={`/app/gmao/v2/parc/${encodeURIComponent(accessory.id)}`}
                    className="rounded-lg border border-[var(--do-border)] px-3 py-2 text-xs font-heading font-bold hover:bg-[var(--do-surface-soft)]"
                    data-testid={`machine-accessory-edit-${accessory.id}`}
                  >
                    Modifier
                  </Link>

                  {mayEdit && (
                    <>
                      <button
                        type="button"
                        onClick={() => detachAccessory(accessory)}
                        disabled={accessorySaving}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--do-border)] px-3 py-2 text-xs font-heading font-bold disabled:opacity-50"
                        data-testid={`machine-accessory-detach-${accessory.id}`}
                      >
                        <LinkBreak size={15} />
                        Détacher
                      </button>

                      <button
                        type="button"
                        onClick={() => setAccessoryArchiveTarget(accessory)}
                        disabled={accessorySaving}
                        className="rounded-lg border border-[var(--do-danger-border)] bg-[var(--do-danger-soft)] px-3 py-2 text-xs font-heading font-bold text-[var(--do-danger)] disabled:opacity-50"
                        data-testid={`machine-accessory-archive-${accessory.id}`}
                      >
                        Archiver
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <MachineAccessoryLinkModal
        open={accessoryLinkOpen}
        machine={machine}
        accessories={accessoryOptions}
        saving={accessorySaving}
        onClose={() => {
          if (!accessorySaving) {
            setAccessoryLinkOpen(false);
          }
        }}
        onLink={linkAccessory}
        onCreate={openAccessoryCreator}
      />

      <MachineFormModal
        open={accessoryCreateOpen}
        mode="create"
        prospects={prospects}
        machines={[machine]}
        catalogItems={catalogItems}
        catalogLoadError={catalogLoadError}
        onReloadCatalog={() => loadCatalogByType("accessory")}
        fixedParentMachineId={machine.id}
        fixedEquipmentType="accessory"
        initialProspectId={machine.prospectId || ""}
        initialLocationStatus={
          machine.prospectId
            ? "installed"
            : "in_stock"
        }
        saving={accessorySaving}
        onClose={() => {
          if (!accessorySaving) {
            setAccessoryCreateOpen(false);
          }
        }}
        onSubmit={createAccessory}
      />

      <MaintenancePlanModal
        open={maintenanceOpen}
        plan={nextMaintenance || null}
        machines={[machine]}
        initialMachineId={machine.id}
        saving={maintenanceSaving}
        onClose={() => {
          if (!maintenanceSaving) {
            setMaintenanceOpen(false);
          }
        }}
        onSubmit={saveMaintenancePlan}
      />

      <MachineFormModal
        open={handpieceOpen}
        mode="create"
        prospects={prospects}
        machines={[machine]}
        catalogItems={catalogItems}
        catalogLoadError={catalogLoadError}
        onReloadCatalog={loadHandpieceCatalog}
        fixedParentMachineId={machine.id}
        initialProspectId={machine.prospectId || ""}
        initialLocationStatus={
          machine.prospectId
            ? "installed"
            : "in_stock"
        }
        saving={saving}
        onClose={() => {
          if (!saving) setHandpieceOpen(false);
        }}
        onSubmit={createHandpiece}
      />

      <MachineIdentityEditModal
        open={editOpen}
        machine={machine}
        prospects={prospects}
        saving={saving}
        onClose={() => {
          if (!saving) setEditOpen(false);
        }}
        onSubmit={saveMachine}
      />

      <MachineMeterModal
        open={meterOpen}
        machines={[machine]}
        initialMachineId={machine.id}
        saving={meterSaving}
        onClose={() => {
          if (!meterSaving) setMeterOpen(false);
        }}
        onSubmit={saveMeter}
      />

      {accessoryArchiveTarget && (
        <MachineArchiveModal
          machine={accessoryArchiveTarget}
          action="archive"
          saving={accessorySaving}
          onClose={() => {
            if (!accessorySaving) {
              setAccessoryArchiveTarget(null);
            }
          }}
          onConfirm={archiveAccessory}
        />
      )}

      {archiveOpen && (
        <MachineArchiveModal
          machine={machine}
          action="archive"
          saving={archiving}
          onClose={() => {
            if (!archiving) setArchiveOpen(false);
          }}
          onConfirm={archiveMachine}
        />
      )}
    </main>
  );
}
